import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { DataTable } from "../../components/data/DataTable";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { NeuDropdown } from "../../components/data/NeuDropdown";
import { createWarehouse, deleteWarehouse, listWarehouses } from "../../api/warehouse";
import { listBranchEmployees } from "../../api/directory";
import { localName } from "../../lib/localName";

const EMPTY = { code: "", name_ar: "", name_en: "", type: "secondary", keeper_id: "" };

// تبويب المخازن: إضافة مخزن مع تعيين أمين مخزن من موظفي الفرع (إلزامي).
export const WhWarehousesTab = ({ branchId }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [open, setOpen] = useState(false);

  const load = () => {
    if (!branchId) { setRows([]); return; }
    listWarehouses(branchId).then((d) => setRows(d.items || [])).catch(() => setRows([]));
    listBranchEmployees(branchId).then(setEmployees).catch(() => setEmployees([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const save = async () => {
    try {
      await createWarehouse({ ...form, branch_id: branchId });
      toast.success(t("warehouse.wh.saved"));
      setForm({ ...EMPTY }); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message_ar
                              || e?.response?.data?.detail?.code || t("errors.generic")); }
  };

  const columns = [
    { key: "code", label: t("warehouse.wh.code"),
      render: (r) => <span className="num font-mono font-bold text-primary">{r.code}</span> },
    { key: "name", label: t("warehouse.wh.name"),
      render: (r) => <span className="text-xs font-bold">{localName(r)}</span> },
    { key: "keeper", label: t("warehouse.wh.keeper"),
      render: (r) => <span className="text-xs">{r.keeper_name || <span className="text-rose-500">{t("warehouse.wh.noKeeper")}</span>}</span> },
    { key: "flags", label: t("purchases.cols.state"),
      render: (r) => (
        <span className={`text-[11px] font-bold ${r.is_default ? "text-primary" : "text-emerald-600"}`}>
          {r.is_default ? t("warehouse.wh.default") : t("purchases.supplierStates.active")}</span>) },
    { key: "act", label: "",
      render: (r) => !r.is_default ? (
        <NeuButton className="px-3 py-2 text-[11px]" data-testid={`wh-del-${r.warehouse_id}`}
                   onClick={async () => { await deleteWarehouse(r.warehouse_id); load(); }}>
          {t("app.delete")}</NeuButton>) : null },
  ];

  return (
    <div className="grid gap-3">
      <div className="flex justify-end">
        <NeuButton variant="primary" data-testid="wh-add-btn" disabled={!branchId}
                   onClick={() => setOpen(!open)}>
          <Plus className="inline h-4 w-4" /> {t("warehouse.wh.add")}</NeuButton>
      </div>
      {open ? (
        <NeuCard className="grid gap-3 p-4 sm:grid-cols-3" data-testid="wh-form">
          {["code", "name_ar", "name_en"].map((k) => (
            <NeuInput key={k} data-testid={`wh-f-${k}`} value={form[k]}
                      placeholder={t(`warehouse.wh.${k}`)}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          ))}
          <NeuDropdown testid="wh-f-keeper" value={form.keeper_id}
                       onChange={(v) => setForm({ ...form, keeper_id: v })}
                       placeholder={t("warehouse.wh.pickKeeper")}
                       options={employees.map((e) => ({ value: e.user_id || e.id,
                         label: `${e.name} · ${e.role || ""}` }))} />
          <NeuInput data-testid="wh-f-type" value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })} />
          <NeuButton variant="primary" data-testid="wh-save"
                     disabled={!form.code || !form.name_ar || !form.keeper_id}
                     onClick={save}>{t("app.save")}</NeuButton>
        </NeuCard>) : null}
      <DataTable testid="wh-list" columns={columns} rows={rows} rowKey={(r) => r.warehouse_id}
                 empty={!branchId ? t("warehouse.wh.pickBranch") : t("warehouse.wh.empty")} />
    </div>
  );
};
